// src/lib/indexedDB-utils.ts
// This module provides utility functions for working with IndexedDB
// It's used to store files and upload tasks persistently

const DB_NAME = "surfsnap-uploads";
const UPLOAD_STORE = "uploads";
const FILE_STORE = "files";
const TASK_STORE = "tasks";
const DB_VERSION = 1;

// Initialize the database
export async function initializeDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;

      // Create object stores if they don't exist
      if (!db.objectStoreNames.contains(FILE_STORE)) {
        db.createObjectStore(FILE_STORE);
      }

      if (!db.objectStoreNames.contains(TASK_STORE)) {
        const taskStore = db.createObjectStore(TASK_STORE, { keyPath: "id" });
        taskStore.createIndex("albumId", "albumId", { unique: false });
        taskStore.createIndex("status", "status", { unique: false });
      }

      if (!db.objectStoreNames.contains(UPLOAD_STORE)) {
        db.createObjectStore(UPLOAD_STORE, { keyPath: "id" });
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

// Store a file in IndexedDB
export async function storeFile(file: File): Promise<string> {
  const db = await initializeDB();
  const fileKey = `file-${Date.now()}-${Math.random()
    .toString(36)
    .substring(2, 10)}`;

  return new Promise((resolve, reject) => {
    const transaction = db.transaction(FILE_STORE, "readwrite");
    const store = transaction.objectStore(FILE_STORE);
    const request = store.put(file, fileKey);

    request.onsuccess = () => resolve(fileKey);
    request.onerror = () => reject(request.error);

    transaction.oncomplete = () => db.close();
  });
}

// Store multiple files in IndexedDB
export async function storeFiles(files: File[]): Promise<string[]> {
  const fileKeys: string[] = [];

  for (const file of files) {
    const key = await storeFile(file);
    fileKeys.push(key);
  }

  return fileKeys;
}

// Create an upload task in IndexedDB
export async function createUploadTask(task: any): Promise<string> {
  const db = await initializeDB();
  const taskId =
    task.id ||
    `task-${Date.now()}-${Math.random().toString(36).substring(2, 10)}`;

  // Add metadata to the task
  const enhancedTask = {
    ...task,
    id: taskId,
    status: "pending",
    progress: 0,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };

  return new Promise((resolve, reject) => {
    const transaction = db.transaction(TASK_STORE, "readwrite");
    const store = transaction.objectStore(TASK_STORE);
    const request = store.put(enhancedTask);

    request.onsuccess = () => resolve(taskId);
    request.onerror = () => reject(request.error);

    transaction.oncomplete = () => db.close();
  });
}

// Get all pending upload tasks
export async function getPendingTasks(): Promise<any[]> {
  const db = await initializeDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction(TASK_STORE, "readonly");
    const store = transaction.objectStore(TASK_STORE);
    const index = store.index("status");
    const request = index.getAll(["pending", "uploading"]);

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);

    transaction.oncomplete = () => db.close();
  });
}

// Get a specific task
export async function getTask(taskId: string): Promise<any> {
  const db = await initializeDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction(TASK_STORE, "readonly");
    const store = transaction.objectStore(TASK_STORE);
    const request = store.get(taskId);

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);

    transaction.oncomplete = () => db.close();
  });
}

// Update a task's status
export async function updateTaskStatus(
  taskId: string,
  status: string,
  progress?: number
): Promise<void> {
  const db = await initializeDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction(TASK_STORE, "readwrite");
    const store = transaction.objectStore(TASK_STORE);
    const request = store.get(taskId);

    request.onsuccess = () => {
      const task = request.result;
      if (task) {
        task.status = status;
        if (progress !== undefined) {
          task.progress = progress;
        }
        task.updatedAt = new Date().toISOString();

        const updateRequest = store.put(task);
        updateRequest.onsuccess = () => resolve();
        updateRequest.onerror = () => reject(updateRequest.error);
      } else {
        reject(new Error(`Task not found: ${taskId}`));
      }
    };

    request.onerror = () => reject(request.error);

    transaction.oncomplete = () => db.close();
  });
}

// Delete a task
export async function deleteTask(taskId: string): Promise<void> {
  const db = await initializeDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction(TASK_STORE, "readwrite");
    const store = transaction.objectStore(TASK_STORE);
    const request = store.delete(taskId);

    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);

    transaction.oncomplete = () => db.close();
  });
}

// Clean up completed tasks older than a certain age
export async function cleanupOldTasks(maxAgeInDays: number = 7): Promise<void> {
  const db = await initializeDB();
  const cutoffDate = new Date();
  cutoffDate.setDate(cutoffDate.getDate() - maxAgeInDays);

  return new Promise((resolve, reject) => {
    const transaction = db.transaction(TASK_STORE, "readwrite");
    const store = transaction.objectStore(TASK_STORE);
    const request = store.openCursor();

    request.onsuccess = (event) => {
      const cursor = (event.target as IDBRequest<IDBCursorWithValue>).result;
      if (cursor) {
        const task = cursor.value;
        if (
          (task.status === "completed" || task.status === "failed") &&
          new Date(task.updatedAt) < cutoffDate
        ) {
          cursor.delete();
        }
        cursor.continue();
      }
    };

    request.onerror = () => reject(request.error);
    transaction.oncomplete = () => {
      db.close();
      resolve();
    };
  });
}
